import {
  convertDocumentTypeKeyToValue,
  convertReceiptTypeKeyToValue,
  useDocumentTypeLabel,
  useReceiptTypeLabel,
} from '@/features/business_documents/hooks';
import { useUploadedAtLabel } from '@/features/invoices/components/Details/Components/ReceivedInvoice/hooks/useReceivedInvoice';
import { useTranslation } from '@/i18n';
import type {
  InvoiceReportBusinessDocumentResponse,
  OfficeMemberDisplay,
} from 'ap-openapi';
import { DATE_FORMAT } from 'date-util';
import { useMemo } from 'react';

export const useBusinessDocumentInfo = (
  data?: InvoiceReportBusinessDocumentResponse
) => {
  const { t } = useTranslation();
  const DocumentTypeLabel = useDocumentTypeLabel();
  const ReceiptTypeLabel = useReceiptTypeLabel();

  const transactionLabel = useUploadedAtLabel(
    data?.transaction_date || '',
    DATE_FORMAT
  );

  const amountLabel = useMemo(() => {
    const amount = data?.amount;
    return amount !== null &&
      amount !== undefined &&
      !Number.isNaN(Number(amount))
      ? Number(amount).toLocaleString() + '円'
      : '';
  }, [data?.amount]);

  const documentTypeLabel = useMemo(() => {
    return data?.document_type
      ? t(DocumentTypeLabel[convertDocumentTypeKeyToValue(data.document_type)])
      : '';
  }, [data?.document_type, DocumentTypeLabel, t]);

  const receiptTypeLabel = useMemo(() => {
    return data?.receipt_type
      ? ReceiptTypeLabel[convertReceiptTypeKeyToValue(data.receipt_type)]
      : '';
  }, [data?.receipt_type, ReceiptTypeLabel]);

  const picsLabel = useMemo(() => {
    return (
      data?.pics
        ?.map((pic: OfficeMemberDisplay) => pic.display_name ?? pic.name)
        .join(', ') || ''
    );
  }, [data?.pics]);

  return useMemo(
    () => ({
      timeStampLabel: data?.timestamp_status || 'unsupported',
      documentTitleLabel: data?.document_title || '',
      documentTypeLabel,
      counterpartyNameLabel: data?.counterparty_name || '',
      picsLabel,
      transactionDateLabel: transactionLabel,
      amountLabel,
      memoLabel: data?.memo || '',
      receiptTypeLabel,
      fileName: data?.filename || '',
    }),
    [
      data?.timestamp_status,
      data?.document_title,
      data?.counterparty_name,
      data?.memo,
      documentTypeLabel,
      picsLabel,
      transactionLabel,
      amountLabel,
      receiptTypeLabel,
      data?.filename,
    ]
  );
};
